'use client';

import { useTransition } from 'react';

import { useRouter } from 'next/navigation';

import { postBioEntityInvalidation } from '@ps/functions/bio/postBioEntityInvalidation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ps/ui/atoms/alert-dialog';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { useDeleteChannel } from '../../hooks/useDeleteChannel';
import type { CommunityChannel } from '../../types/CommunityChannel';

interface Props {
  channel: CommunityChannel;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /**
   * Fired after a successful delete — the caller (channel info
   * popover) closes itself so the user lands back on the channel
   * list rather than the now-defunct detail view.
   */
  onDeleted?: () => void;
}

/**
 * Confirmation dialog for "Delete Channel" in the info popover.
 * Deletes via the API, navigates back to the community landing (the
 * detail URL would 404 once the channel doc is gone, and the
 * Firestore channel-list sub will remove the row from the master
 * pane shortly after).
 */
export function DeleteChannelDialog({
  channel,
  open,
  onOpenChange,
  onDeleted,
}: Props) {
  const router = useRouter();
  const { homeHref } = useCommunityContext();
  const deleteChannel = useDeleteChannel({ channelId: channel.id });
  const [pending, startTransition] = useTransition();

  const onConfirm = () => {
    if (pending) return;
    startTransition(async () => {
      try {
        await deleteChannel();
        // Deleting may drop the creator to zero channels — bust the visitor
        // bio's community-channels gate so the "Community Channels" main action
        // button hides (session-derived uid, so the owner busts only itself).
        postBioEntityInvalidation({ kind: 'communityChannels' });
        onDeleted?.();
        router.push(homeHref);
      } catch (error) {
        console.error('delete channel failed', error);
      }
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="text-content-1 border-line bg-surface-1">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete channel?</AlertDialogTitle>
          <AlertDialogDescription className="text-content-2">
            <strong className="text-content-1">{channel.name}</strong> and all
            of its messages will be permanently removed. This can&rsquo;t be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={pending}
            className="text-content-1 border-line-strong bg-surface-1 hover:bg-brand-hover-soft"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={onConfirm}
            className="bg-danger text-content-on-danger hover:bg-red-600"
          >
            {pending ? 'Deleting…' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
